import { decodeFunctionResult, encodeFunctionData, recoverAddress, type Hex } from "viem";
import { ponsRewardNftABI } from "../../../abis/pons-reward-nft";
import { decodeReferralMint, sameReferralIdentity, type ReferralMintEvidence, type ReferralMintIdentity } from "../contracts/referral-mint";
import type { ReferralAuthorization } from "../api/referral-schemas";
import type { PreparedNftTransaction } from "../../../wallet/transaction-preflight";
import type { Eip1193Provider } from "../../../wallet/eip1193";
import { readRpc, type RpcReadOptions } from "../../../wallet/read-rpc";
import { contractReadError } from "../../../contracts/contract-errors";

export type PreparedReferralMint = { identity: ReferralMintIdentity; evidence: ReferralMintEvidence; result: ReferralAuthorization };

export function assertReferralMintCall(input: PreparedNftTransaction) {
  const referral = input.referralMint;
  if (!referral || input.taskMint || input.valueWei !== 0n || !referral.evidence.executionEnabled ||
    input.data !== referral.result.call.data || input.to.toLowerCase() !== referral.result.call.to.toLowerCase() ||
    input.mintSigner?.toLowerCase() !== referral.evidence.signer.toLowerCase()) throw new Error("Unreviewed referral mint");
  const decoded = decodeReferralMint(referral.result, { wallet: input.account, sourceToken: referral.evidence.sourceToken,
    collection: input.to, chainId: input.chainId, milestone: referral.identity.milestone }, referral.evidence);
  if (!decoded.identity || !sameReferralIdentity(decoded.identity, referral.identity)) throw new Error("Referral tracked identity mismatch");
  return decoded;
}

export async function preflightReferralMint(provider: Eip1193Provider, input: PreparedNftTransaction,
  assertCurrent: () => void, options: RpcReadOptions) {
  const { authorization, signature } = assertReferralMintCall(input);
  assertCurrent();
  await input.referralMint!.evidence.verifyDeployment(provider, assertCurrent, options);
  assertCurrent();
  let raw: unknown;
  try { raw = await readRpc(provider, { method: "eth_call", params: [{ to: input.to,
    data: encodeFunctionData({ abi: ponsRewardNftABI, functionName: "mintAuthorizationDigest", args: [authorization] }) }, "latest"] }, options); }
  catch (error) { assertCurrent(); throw contractReadError(error); }
  assertCurrent();
  let recovered: string | null = null;
  try {
    if (typeof raw === "string") recovered = await recoverAddress({
      hash: decodeFunctionResult({ abi: ponsRewardNftABI, functionName: "mintAuthorizationDigest", data: raw as Hex }), signature });
  } catch { /* Never expose signature or calldata in transaction history. */ }
  assertCurrent();
  if (!recovered || recovered.toLowerCase() !== input.mintSigner?.toLowerCase()) throw new Error("InvalidAuthorizationSignature: Referral signer/domain mismatch");
}
